import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface EstadoEntrada {
  pedido: { aoCobrir: () => void } | null;
  /**
   * Toca a entrada e, com a tela coberta, chama `aoCobrir` (o login de fato:
   * o layout do login redireciona sozinho quem já entrou). Quem pede menos
   * movimento entra direto.
   */
  entrar: (aoCobrir: () => void) => void;
  fim: () => void;
}

export const useEntrada = create<EstadoEntrada>((set) => ({
  pedido: null,
  entrar: (aoCobrir) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      aoCobrir();
      return;
    }
    set({ pedido: { aoCobrir } });
  },
  fim: () => set({ pedido: null }),
}));

/* Os mesmos caracteres, cores e medidas da abertura do site do DevClub
   (devclub-lp/parts/js/00-intro.js). */
const CHARS = '01<>{}/=;:+*#$%&@ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇ';
const LIMA = '#A3E635';
const LIMA_RGB = '163,230,53';
const CLARO = '#F2FFD6';
const BLOCO = 16;
const CELULA = 15;
const sorteia = () => CHARS[(Math.random() * CHARS.length) | 0];

/* O cadeado é o "lock" do pixelarticons (MIT), na grade de 24 dele, em
   retângulos [x, y, largura, altura]. A alça é o que sobe ao abrir; o miolo
   é o buraco da chave, que acende quando abre. */
const GRADE = 24;
const ALCA = [
  [9, 2, 6, 2],
  [7, 4, 2, 4],
  [15, 4, 2, 4],
];
const CORPO = [
  [4, 8, 16, 2],
  [4, 20, 16, 2],
  [4, 10, 2, 10],
  [18, 10, 2, 10],
];
const MIOLO = [[11, 13, 2, 4]];
/* Aberto, a alça sobe 3 módulos e a perna direita continua presa no corpo
   (como no "lock-open" do mesmo pacote): estes módulos são o pedaço dela que
   sai de dentro do corpo. Sem eles a alça subia inteira e flutuava solta. */
const SUBIDA = 3;
const PERNA = [[15, 8 - SUBIDA, 2, SUBIDA]];

type Parte = 'alca' | 'corpo' | 'miolo' | 'perna';
const celulas = (rets: number[][], parte: Parte) =>
  rets.flatMap(([x, y, l, a]) =>
    Array.from({ length: l * a }, (_, k) => ({
      gx: x + (k % l),
      gy: y + Math.floor(k / l),
      parte,
    })),
  );
const CADEADO = [
  ...celulas(ALCA, 'alca'),
  ...celulas(CORPO, 'corpo'),
  ...celulas(MIOLO, 'miolo'),
];
const ABERTO = [...CADEADO, ...celulas(PERNA, 'perna')];

type Fase = 'fora' | 'tocando';

/**
 * A entrada na plataforma depois do login. Rodada 6 (29/10): a primeira
 * versão (um ícone de contorno sobre um véu, a alça inclinando e depois uma
 * onda que não tinha nada a ver com ele) "ficou meio paia". Esta segue a lei
 * da abertura da LP: UM morph só, uma matéria, nenhum corte.
 *
 *   1. a tela escurece e o código voa de todo canto até a grade do cadeado:
 *      cada caractere que chega vira um módulo (o cadeado se materializa);
 *   2. a alça sobe com um estalo (passa um tico e assenta), o corpo pisca
 *      claro uma vez e o buraco da chave fica aceso — "> acesso liberado";
 *   3. a onda de blocos desce e engole o cadeado: cada módulo cresce até o
 *      tamanho do bloco da onda quando a frente passa por ele, com a chuva de
 *      código dentro da onda;
 *   4. com a tela coberta a plataforma entra por baixo, e os blocos se apagam
 *      descendo, no mesmo sentido em que cobriram.
 *
 * Um canvas só. Mora uma vez no App.
 */
export function EntradaDoCadeado() {
  const pedido = useEntrada((s) => s.pedido);
  const fim = useEntrada((s) => s.fim);
  const [fase, setFase] = useState<Fase>('fora');
  const tela = useRef<HTMLCanvasElement>(null);
  const chegou = useRef<(() => void) | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!pedido) return;
    const cv = tela.current;
    const cx = cv?.getContext('2d');
    if (!cv || !cx) {
      // A animação é opcional; a autenticação deve concluir mesmo sem canvas.
      if (useEntrada.getState().pedido === pedido) {
        fim();
        pedido.aoCobrir();
      }
      return;
    }
    let vivo = true;
    setFase('tocando');
    const dpr = Math.min(2, devicePixelRatio || 1);
    const L = innerWidth;
    const A = innerHeight;
    cv.width = Math.round(L * dpr);
    cv.height = Math.round(A * dpr);
    cx.setTransform(dpr, 0, 0, dpr, 0, 0);
    cx.clearRect(0, 0, L, A);

    // o fundo é o da plataforma (o cinza do tema escuro), não um preto à parte
    const variavel = getComputedStyle(document.documentElement)
      .getPropertyValue('--background')
      .trim();
    const FUNDO = `hsl(${variavel || '206 7% 13%'})`;

    // módulo do cadeado: ~30% do lado menor da tela, inteiro em pixels
    const m = Math.max(5, Math.round((Math.min(L, A) * 0.3) / GRADE));
    const lado = m * GRADE;
    const ox = Math.round((L - lado) / 2);
    const oy = Math.round((A - lado) / 2 - m * 1.5);
    const subida = m * SUBIDA;

    const P = CADEADO.map((c) => ({
      ...c,
      x: Math.random() * L,
      y: Math.random() * A,
      ch: sorteia(),
      atraso: Math.random() * 0.38,
    }));
    type Modulo = (typeof P)[number];
    // aberto: os mesmos módulos mais o pedaço da perna que saiu do corpo
    const PA = [
      ...P,
      ...ABERTO.filter((c) => c.parte === 'perna').map((c) => ({
        ...c,
        x: 0,
        y: 0,
        ch: '',
        atraso: 0,
      })),
    ];

    const espera = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const quadro = (fn: (s: number) => boolean) =>
      new Promise<void>((pronto) => {
        const t0 = performance.now();
        const passo = (t: number) => {
          if (vivo && fn((t - t0) / 1000)) requestAnimationFrame(passo);
          else pronto();
        };
        requestAnimationFrame(passo);
      });
    const facil = (t: number) => 1 - Math.pow(1 - t, 3);
    // passa um tico e volta (o estalo da alça)
    const estalo = (t: number) =>
      1 + 3.2 * Math.pow(t - 1, 3) + 2.2 * Math.pow(t - 1, 2);

    /** Centro de cada módulo (a alça sobe `alca` px). */
    const pos = (q: Modulo, alca: number) => ({
      x: ox + q.gx * m + m / 2,
      y: oy + q.gy * m + m / 2 - (q.parte === 'alca' ? alca : 0),
    });

    const fundo = (alpha = 1) => {
      cx.globalAlpha = alpha;
      cx.fillStyle = FUNDO;
      cx.fillRect(0, 0, L, A);
      cx.globalAlpha = 1;
    };

    let mensagem = '';
    const escrever = (alpha: number) => {
      if (!mensagem || alpha <= 0) return;
      cx.font = `600 ${Math.max(12, Math.round(m * 1.25))}px 'JetBrains Mono', ui-monospace, monospace`;
      cx.textAlign = 'center';
      cx.textBaseline = 'top';
      cx.fillStyle = `rgba(${LIMA_RGB},${alpha.toFixed(3)})`;
      cx.fillText(mensagem, L / 2, oy + lado + m * 1.6);
    };

    /* 1. O CADEADO SE MATERIALIZA: a tela escurece e cada caractere caminha
          até o seu módulo; ao chegar para de girar e vira bloco. */
    const materializar = (dur: number) =>
      quadro((s) => {
        const p = Math.min(1, s / dur);
        cx.clearRect(0, 0, L, A);
        fundo(Math.min(1, s / 0.22));
        cx.font = `600 ${Math.round(m * 0.9)}px ui-monospace, monospace`;
        cx.textAlign = 'center';
        cx.textBaseline = 'middle';
        for (const q of P) {
          const t = Math.max(0, Math.min(1, (p - q.atraso) / (1 - q.atraso)));
          const e = facil(t);
          const alvo = pos(q, 0);
          const x = q.x + (alvo.x - q.x) * e;
          const y = q.y + (alvo.y - q.y) * e;
          if (t < 0.82) {
            if (Math.random() < 0.5) q.ch = sorteia();
            cx.fillStyle = t < 0.4 ? `rgba(${LIMA_RGB},.55)` : LIMA;
            cx.fillText(q.ch, x, y);
          } else {
            // o caractere vira o módulo: cresce até o quadrado, sem piscar
            const g = (t - 0.82) / 0.18;
            const s2 = m * (1 - 0.12 * (1 - g));
            cx.fillStyle = LIMA;
            cx.fillRect(x - s2 / 2, y - s2 / 2, s2, s2);
          }
        }
        return p < 1;
      });

    /* 2. ABRE: a alça sobe com estalo; no topo o corpo pisca claro uma vez
          e o buraco da chave fica aceso. */
    const abrir = (dur: number) =>
      quadro((s) => {
        const p = Math.min(1, s / dur);
        const subiu = subida * estalo(Math.min(1, p / 0.7));
        const pisca = p > 0.55 && p < 0.66;
        const miolo = Math.max(0, Math.min(1, (p - 0.55) / 0.2));
        cx.clearRect(0, 0, L, A);
        fundo();
        for (const q of P) {
          const { x, y } = pos(q, subiu);
          if (q.parte === 'miolo' && miolo > 0) {
            cx.fillStyle = CLARO;
            cx.globalAlpha = 0.4 + miolo * 0.6;
          } else {
            cx.fillStyle = pisca && q.parte === 'corpo' ? CLARO : LIMA;
          }
          cx.fillRect(x - m / 2, y - m / 2, m, m);
          cx.globalAlpha = 1;
        }
        // a perna direita escorrega para fora do corpo junto com a alça
        if (subiu > 0) {
          cx.fillStyle = LIMA;
          cx.fillRect(ox + 15 * m, oy + 8 * m - subiu, 2 * m, subiu);
        }
        if (p > 0.6) {
          const frase = '> acesso liberado';
          mensagem = frase.slice(
            0,
            Math.min(
              frase.length,
              Math.round(((p - 0.6) / 0.35) * frase.length),
            ),
          );
        }
        escrever(0.9);
        return p < 1;
      });

    /* 3. A ONDA COBRE: a frente de blocos desce irregular; cada módulo do
          cadeado só se solta quando a frente passa pela altura dele e CRESCE
          até o bloco da onda, fundindo-se nela. A chuva vem dentro da onda. */
    const cobrir = (dur: number) => {
      const cols = Math.ceil(L / BLOCO);
      const lins = Math.ceil(A / BLOCO);
      const atraso = new Float32Array(cols * lins);
      for (let y = 0, k = 0; y < lins; y++)
        for (let x = 0; x < cols; x++, k++)
          atraso[k] = (y / lins) * 0.8 + Math.random() * 0.14;
      const colunas = Array.from({ length: Math.ceil(L / CELULA) }, () => ({
        y: -Math.random() * A * 0.5,
        v: 8 + Math.random() * 14,
      }));
      return quadro((s) => {
        const p = Math.min(1.1, (s / dur) * 1.1);
        cx.clearRect(0, 0, L, A);
        fundo();
        const frente = -A * 0.12 + Math.min(1, p) * A * 1.24;
        escrever(0.9 - p * 2);
        for (const q of PA) {
          const { x, y } = pos(q, subida);
          const t = (frente - y) / (m * 2.2);
          cx.fillStyle = q.parte === 'miolo' ? CLARO : LIMA;
          if (t <= 0) {
            cx.fillRect(x - m / 2, y - m / 2, m, m);
            continue;
          }
          const g = Math.min(1, t);
          const s2 = m + (BLOCO - m) * g;
          cx.fillRect(x - s2 / 2, y + g * m * 0.35 - s2 / 2, s2, s2);
        }
        // a chuva de código, só dentro da faixa já coberta
        cx.font = `600 ${CELULA - 2}px ui-monospace, monospace`;
        cx.textAlign = 'left';
        cx.textBaseline = 'top';
        colunas.forEach((c, i) => {
          if (c.y > frente + CELULA * 3) return;
          cx.fillStyle = CLARO;
          cx.fillText(sorteia(), i * CELULA, c.y);
          cx.fillStyle = `rgba(${LIMA_RGB},.7)`;
          cx.fillText(sorteia(), i * CELULA, c.y - CELULA);
          c.y += c.v;
          if (c.y > A + CELULA * 2) c.y = -CELULA * (2 + Math.random() * 6);
        });
        // a frente de blocos, por cima de tudo
        for (let y = 0, k = 0; y < lins; y++)
          for (let x = 0; x < cols; x++, k++) {
            const t = (p - atraso[k]) / 0.16;
            if (t <= 0) continue;
            cx.fillStyle = t < 1 ? `rgba(${LIMA_RGB},${t.toFixed(3)})` : LIMA;
            cx.fillRect(x * BLOCO, y * BLOCO, BLOCO, BLOCO);
          }
        return p < 1.1;
      });
    };

    /* 4. OS BLOCOS SE APAGAM descendo, no mesmo sentido da onda que cobriu. */
    const revelar = (dur: number) => {
      const cols = Math.ceil(L / BLOCO);
      const lins = Math.ceil(A / BLOCO);
      const atraso: number[] = [];
      for (let y = 0; y < lins; y++)
        for (let x = 0; x < cols; x++)
          atraso.push((y / lins) * 0.78 + Math.random() * 0.16);
      return quadro((s) => {
        const p = Math.min(1.16, (s / dur) * 1.16);
        for (let y = 0, k = 0; y < lins; y++)
          for (let x = 0; x < cols; x++, k++) {
            const t = (p - atraso[k]) / 0.22;
            if (t <= 0) continue;
            cx.clearRect(x * BLOCO, y * BLOCO, BLOCO, BLOCO);
            if (t < 1) {
              cx.fillStyle = `rgba(${LIMA_RGB},${(1 - t).toFixed(3)})`;
              cx.fillRect(x * BLOCO, y * BLOCO, BLOCO, BLOCO);
            }
          }
        return p < 1.16;
      });
    };

    (async () => {
      await materializar(0.85);
      if (!vivo) return;
      // o fundo do canvas já cobre a tela: a plataforma entra por baixo
      // enquanto o cadeado abre, e a tela lima cheia dura só um instante
      const chegada = new Promise<void>((r) => {
        chegou.current = r;
        setTimeout(r, 5000);
      });
      pedido.aoCobrir();
      await espera(90);
      await abrir(0.6);
      if (!vivo) return;
      await espera(120);
      await cobrir(0.9);
      if (!vivo) return;
      await chegada;
      chegou.current = null;
      await espera(60);
      await revelar(0.85);
      cx.clearRect(0, 0, L, A);
      setFase('fora');
      fim();
    })();
    return () => {
      vivo = false;
    };
  }, [pedido, fim]);

  useEffect(() => {
    if (location.pathname !== '/login') chegou.current?.();
  }, [location.pathname]);

  return (
    <div
      aria-hidden="true"
      className={cn(
        'fixed inset-0 z-[9998]',
        fase === 'fora' && 'pointer-events-none invisible',
      )}
    >
      <canvas ref={tela} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
